import { AddIcon, CartIcon, CrossSmallIcon, SchoolIcon } from '@bi-digital/icons';
import {
    Alert,
    Button,
    Dialog,
    DialogCloseButton,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTrigger,
    DialogTriggerContext,
    useMediaQuery,
} from '@bi-digital/react';
import { useSessionStorage } from '@bino/utils/hooks';
import classNames from 'classnames';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { deleteCartItem, JUST_SIGNED_IN_KEY } from '../../api';
import { useCartContext } from '../../context/CartContext';
import type { BiaxCartItem } from '../../types';
import styles from './cart.module.css';
import CartCardItem from './CartCardItem/cartCardItem';
import { CartFooter } from './CartFooter/CartFooter';

export default function Cart() {
    const { t } = useTranslation(['cart', 'common']);
    const [justSignedIn, setJustSignedIn] = useSessionStorage(JUST_SIGNED_IN_KEY, false);

    const { isAuthorized, cart, refreshCart, productSearchUrl, disableCart, cartOpen, setCartOpen } = useCartContext();

    const [deleteError, setDeleteError] = useState<string | null>(null);
    const [itemBeingDeleted, setItemBeingDeleted] = useState<string | null>(null);
    const count = cart.items.length === 0 ? undefined : cart.items.length;
    const isSmallScreen = useMediaQuery('(width < 768px)');

    const removeFromShoppingCart = async (item: BiaxCartItem) => {
        try {
            setItemBeingDeleted(item.bannerId);
            setDeleteError(null);

            await deleteCartItem(item.bannerId);
            const updatedItems = cart.items.filter(({ bannerId }) => bannerId !== item.bannerId);

            await refreshCart((c) => ({
                ...c,
                items: updatedItems,
                biaxCartDetailPageUrl: c?.biaxCartDetailPageUrl || '',
            }));
            setItemBeingDeleted(null);
        } catch {
            setDeleteError(item.bannerId);
            setItemBeingDeleted(null);
        }
    };

    useEffect(() => {
        if (!justSignedIn) {
            return;
        }

        const timeoutId = setTimeout(() => setJustSignedIn(false), 10000);
        return () => clearTimeout(timeoutId);
    }, [justSignedIn, setJustSignedIn]);

    if (disableCart) {
        return null;
    }

    return (
        <DialogTriggerContext>
            <DialogTrigger
                className={styles.cartButton}
                variant={count ? 'flat' : 'text'}
                size='medium'
                startIcon={<CartIcon title={t('cart:AriaLabelShoppingCart')} />}
            >
                {(count || !isSmallScreen) && (count ? count : t('cart:shoppingcart'))}
            </DialogTrigger>

            {justSignedIn && isAuthorized && (
                <Alert
                    size='large'
                    closeButton={
                        <Button icon variant='text' onClick={() => setJustSignedIn(false)}>
                            <CrossSmallIcon aria-label={t('common:close')} />
                        </Button>
                    }
                    className={styles.signedInConfirmation}
                    role='region'
                    icon={<></>}
                    data-color-scheme='light'
                >
                    <div>
                        <h3>{t('cart:justSignedInAlertTitle')}</h3>
                        <p>{t('cart:justSignedInAlertMessage')}</p>
                    </div>
                </Alert>
            )}

            <Dialog
                open={cartOpen}
                className={styles.cartDrawer}
                anchor={isSmallScreen ? 'bottom' : 'right'}
                onClose={() => setCartOpen(false)}
                data-color-scheme='light'
            >
                <DialogCloseButton className={styles.closeButton} aria-label={t('common:close')} />
                <DialogHeader className={styles.cartHeader}>
                    <CartIcon size='large' aria-hidden='true' style={{ color: 'var(--bi-neutral-90)' }} />
                    <h2 className={styles.cartHeaderTitle}>
                        {t('cart:shoppingcart')} {count && <span className={styles.cartItemCount}>({count})</span>}
                    </h2>
                </DialogHeader>

                <DialogContent
                    className={classNames(styles.cartContent, {
                        [`${styles.emptyContentContainer}`]: cart.items.length === 0,
                    })}
                >
                    {cart.items.length > 0 && (
                        <div className={styles.cartItems}>
                            {cart.items.map((item) => (
                                <CartCardItem
                                    key={item.bannerId}
                                    item={item}
                                    deleteError={deleteError}
                                    itemBeingDeleted={itemBeingDeleted}
                                    removeFromCart={removeFromShoppingCart}
                                />
                            ))}
                            {productSearchUrl && (
                                <Button
                                    variant='text'
                                    className={styles.moreStudiesButton}
                                    startIcon={<AddIcon aria-hidden='true' />}
                                    asChild
                                >
                                    <a href={productSearchUrl}>{t('cart:moreStudies')}</a>
                                </Button>
                            )}
                        </div>
                    )}

                    {cart.items.length === 0 && (
                        <div className={styles.emptyCart}>
                            <SchoolIcon aria-hidden='true' size='2xl' />
                            <p className={styles.emptyCartHeading}>{t('cart:emptyCart')}</p>
                            <p className={styles.emptyCartLabel}>{t('cart:emptyCartMessage')}</p>
                            <Button
                                asChild
                                startIcon={<AddIcon aria-hidden='true' />}
                                variant={isAuthorized ? 'filled' : 'flat'}
                                size={isSmallScreen ? 'medium' : 'large'}
                            >
                                <a href={productSearchUrl}>{t('cart:addStudies')}</a>
                            </Button>
                        </div>
                    )}
                </DialogContent>

                <DialogFooter className={styles.cartFooter}>
                    <CartFooter />
                </DialogFooter>
            </Dialog>
        </DialogTriggerContext>
    );
}
